import type { AlignmentReviewEntitlementBlock } from './useAlignmentReview';

/**
 * Pure "which state does the alignment-review deck actually show" decision —
 * same house idiom as refineThreadsSource.ts's resolveThreadsDisplayState /
 * refineDnaReveal.ts's resolveRefineDnaReveal: framework-free, no fetch, no
 * React, so the CLEANUP-CREW gating pass's new render branches (blocked /
 * checking / resting / list) are directly unit-testable rather than living
 * only as inline JSX conditionals in AlignmentReviewDeck.tsx.
 *
 * THE DECISION THIS MODULE PINS (the owner's directive: "it needs to be a
 * feature we can gate, but it needs to be there"):
 *
 *   'blocked'  — requireEntitlement('tailoring') 402'd the last fetch. Wins
 *                over every other state (a blocked user sees WHY, never a
 *                stale flags list or a resting-state lie about their corpus
 *                being "properly filed" when the truth is the check never
 *                ran).
 *   'checking' — a fetch (initial OR a manual "Check again" re-run) is in
 *                flight AND there are no flags to show yet. Distinct from a
 *                flags-list refresh-in-progress (see resolveCheckAgainBusy
 *                below) — a first-load/empty-corpus check replaces the whole
 *                card body with "Checking your stories…", while a re-check
 *                that already has a flags list to show keeps that list up
 *                and only busies the header's own control (never yanks
 *                already-visible cards out from under the user mid-refresh).
 *   'resting'  — not blocked, not loading, and zero open flags: the honest
 *                "your stories all look properly filed" state — THIS is the
 *                fix for the surface being invisible-by-omission before this
 *                lane (the deck used to `return null` here).
 *   'list'     — one or more open flags: the existing card-per-flag deck.
 *
 * Ordering is deliberate and total: blocked > checking > resting > list is
 * WRONG for the loading+flags case (a user re-checking with existing flags
 * already on screen should keep seeing them, busied, not lose them to a
 * generic "Checking…" wall) — so 'checking' only wins when flags is EMPTY;
 * loading with a non-empty flags array resolves to 'list' (the header's own
 * "Checking…" control conveys the in-flight state instead).
 */
export type AlignmentReviewDisplayState = 'blocked' | 'checking' | 'resting' | 'list';

export function resolveAlignmentReviewDisplayState(
  loading: boolean,
  flagCount: number,
  entitlementBlock: AlignmentReviewEntitlementBlock | null,
): AlignmentReviewDisplayState {
  if (entitlementBlock) return 'blocked';
  if (loading && flagCount === 0) return 'checking';
  if (flagCount === 0) return 'resting';
  return 'list';
}

/** Whether the header's own "Check again" control (the flags-exist branch)
 *  should render its busy/disabled treatment. Same underlying `loading` flag
 *  as resolveAlignmentReviewDisplayState, but this is the SEPARATE question
 *  "is a re-check in flight while a list is already showing" — the 'list'
 *  state itself doesn't carry loading in its return value (a string can't
 *  carry two independent facts honestly), so the component asks this
 *  directly rather than inferring it from the display state alone. */
export function isCheckAgainBusy(loading: boolean): boolean {
  return loading;
}
